"use client";
import React, { useState, useRef, useEffect } from "react";
// TODO: Replace next/dynamic - use React.lazy instead for dynamic imports
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import {Button} from "@/components/ui/button";

// Imports dynamiques pour chaque composant individuellement
// TODO: Replace with React.lazy - next/dynamic not available in Vite
// const HTMLFlipBook = React.lazy(() => import("react-pageflip"));
// const Document = React.lazy(() => import("react-pdf").then(mod => ({ default: mod.Document })));
// const Page = React.lazy(() => import("react-pdf").then(mod => ({ default: mod.Page })));
import HTMLFlipBook from "react-pageflip";
import { Document, Page } from "react-pdf";

// Import du module pdfjs sans chemin spécifique
// La version correcte sera déterminée automatiquement
const PDFWorker = () => {
    useEffect(() => {
        const loadPdfWorker = async () => {
            try {
                const pdfjs = await import("react-pdf");
                // Configure le worker PDF.js
                pdfjs.pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;
            } catch (error) {
                console.error("Erreur lors du chargement du PDF.js worker:", error);
            }
        };

        loadPdfWorker();
    }, []);

    return null;
};

interface FlippingBookPDFProps {
    pdfUrl: string;
    title?: string;
    downloadFileName?: string;
}

interface PageDimensions {
    width: number;
    height: number;
    isLandscape: boolean;
}

const FlippingBookPDF: React.FC<FlippingBookPDFProps> = ({
                                                             pdfUrl,
                                                             title,
                                                             downloadFileName = "document.pdf",
                                                         }) => {
    const [numPages, setNumPages] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const bookRef = useRef<any>(null);
    const [isClient, setIsClient] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);
    const [pageDimensions, setPageDimensions] = useState<PageDimensions>({
        width: 0,
        height: 0,
        isLandscape: false
    });
    const containerRef = useRef<HTMLDivElement>(null);
    const [pdfDocument, setPdfDocument] = useState<any>(null);

    // Définir isClient à true une fois que le composant est monté côté client
    useEffect(() => {
        setIsClient(true);

        // Mesurer la largeur du conteneur
        if (containerRef.current) {
            const width = containerRef.current.clientWidth;
            setContainerWidth(width);
        }

        // Mettre à jour la largeur lors du redimensionnement
        const handleResize = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                setContainerWidth(width);
                updatePageDimensions(width);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Effet pour mettre à jour les dimensions quand le document PDF est chargé
    useEffect(() => {
        if (pdfDocument && containerWidth > 0) {
            updatePageDimensions(containerWidth);
        }
    }, [pdfDocument, containerWidth]);

    // Fonction pour déterminer les dimensions de la page en fonction de l'orientation du PDF
    const updatePageDimensions = async (containerWidth: number) => {
        if (!pdfDocument) return;

        try {
            // Obtenir les dimensions de la première page
            const page = await pdfDocument.getPage(1);
            const viewport = page.getViewport({ scale: 1 });

            const isLandscape = viewport.width > viewport.height;
            const baseWidth = Math.floor(containerWidth * 0.9);

            let width, height;

            if (isLandscape) {
                // Format paysage
                width = baseWidth;
                height = Math.floor(width * (viewport.height / viewport.width));
            } else {
                // Format portrait
                height = Math.floor(baseWidth * 1.33); // Ratio portrait standard
                width = Math.floor(height * (viewport.width / viewport.height));

                // Si la largeur calculée dépasse la largeur du conteneur, ajuster
                if (width > baseWidth) {
                    width = baseWidth;
                    height = Math.floor(width * (viewport.height / viewport.width));
                }
            }

            setPageDimensions({
                width,
                height,
                isLandscape
            });

        } catch (error) {
            console.error("Erreur lors de la détermination des dimensions:", error);
        }
    };

    function onDocumentLoadSuccess(pdfData: any) {
        console.log(`PDF loaded successfully with ${pdfData.numPages} pages`);
        setNumPages(pdfData.numPages);
        setLoading(false);
        setPdfError(null);
        setPdfDocument(pdfData);
    }

    function onDocumentLoadError(error: Error) {
        console.error("Error loading PDF:", error);
        setLoading(false);
        setPdfError(`Erreur de chargement du PDF: ${error.message}`);
    }

    const handleDownload = () => {
        // Pour les fichiers dans le dossier public, on peut créer un lien direct
        window.open(pdfUrl, '_blank');
    };

    const nextPage = () => {
        if (bookRef.current && currentPage < (numPages || 0)) {
            bookRef.current.pageFlip().flipNext();
            setCurrentPage((prev) => prev + 1);
        }
    };

    const prevPage = () => {
        if (bookRef.current && currentPage > 1) {
            bookRef.current.pageFlip().flipPrev();
            setCurrentPage((prev) => prev - 1);
        }
    };

    // Préparer l'URL absolue pour le PDF
    const absolutePdfUrl = isClient && !pdfUrl.startsWith('http')
        ? `${window.location.origin}${pdfUrl}`
        : pdfUrl;

    return (
        <div className="flex flex-col items-center w-full">
            {/* Charge le worker PDF.js */}
            <PDFWorker />

            {title && (
                <h2 className="text-2xl font-bold mb-4 font-exo text-center">{title}</h2>
            )}

            <div ref={containerRef} className="relative w-full //max-w-4xl">
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 z-10">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                )}

                {pdfError && (
                    <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
                        {pdfError}
                        <p className="mt-2">
                            Vous pouvez toujours télécharger le document directement.
                        </p>
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={prevPage}
                        disabled={currentPage <= 1}
                        className={`p-2 rounded-full ${
                            currentPage <= 1 ? "text-gray-400" : "text-primary hover:bg-gray-100"
                        }`}
                        aria-label="Page précédente"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    <span className="text-sm">
                        Page {currentPage} sur {numPages || "..."}
                    </span>

                    <button
                        onClick={nextPage}
                        disabled={currentPage >= (numPages || 0)}
                        className={`p-2 rounded-full ${
                            currentPage >= (numPages || 0) ? "text-gray-400" : "text-primary hover:bg-gray-100"
                        }`}
                        aria-label="Page suivante"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>

                <div className="border rounded-lg shadow-lg overflow-hidden">
                    {isClient && containerWidth > 0 && (
                        <Document
                            file={absolutePdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                                </div>
                            }
                            error={
                                <div className="p-16 text-center text-gray-500">
                                    Impossible de charger le PDF. Veuillez essayer de le télécharger directement.
                                </div>
                            }
                        >
                            {numPages && !loading && pageDimensions.width > 0 && (
                                // @ts-ignore
                                <HTMLFlipBook
                                    width={pageDimensions.width}
                                    height={pageDimensions.height}
                                    size="stretch"
                                    minWidth={300}
                                    maxHeight={1533}
                                    flippingTime={200}
                                    usePortrait={!pageDimensions.isLandscape}
                                    autoSize={true}
                                    startZIndex={0}
                                    drawShadow={true}
                                    showCover={true}
                                    className="mx-auto"
                                    onFlip={(e: any) => setCurrentPage(e.data + 1)}
                                    ref={bookRef}
                                >
                                    {Array.from(new Array(numPages), (_, index) => (
                                        <div key={`page_${index + 1}`} className="bg-white shadow-inner">
                                            <Page
                                                pageNumber={index + 1}
                                                width={(pageDimensions.width / 2) + (pageDimensions.isLandscape ? 100 : 150)}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </div>
                                    ))}
                                </HTMLFlipBook>
                            )}
                        </Document>
                    )}
                </div>

                <div className="mt-6 flex justify-center">
                    <Button
                        onClick={handleDownload}
                        variant={"yellow"}
                    >
                        <Download size={18} />
                        Télécharger le document
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FlippingBookPDF;
